import React from 'react';
import { Link } from 'react-router-dom';
import { usePostsStore, useAnalyticsStore, usePlatformsStore, useAuthStore } from '../stores';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  Heart,
  MousePointerClick,
  Users,
  Calendar,
  Clock,
  ArrowRight,
  Plus,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Music,
  Pin,
  AtSign,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const platformIcons = {
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
  youtube: Youtube,
  tiktok: Music,
  pinterest: Pin,
  threads: AtSign,
};

const platformColors = {
  twitter: 'text-[#1DA1F2]',
  instagram: 'text-[#E4405F]',
  linkedin: 'text-[#0A66C2]',
  facebook: 'text-[#1877F2]',
  youtube: 'text-[#FF0000]',
  tiktok: 'text-gray-900 dark:text-white',
  pinterest: 'text-[#E60023]',
  threads: 'text-gray-900 dark:text-white',
};

function StatCard({ title, value, change, changeType, icon: Icon }) {
  const isPositive = changeType === 'positive';
  
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className={`p-2.5 rounded-xl ${
          isPositive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
        }`}>
          <Icon className={`w-5 h-5 ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`} />
        </div>
      </div>
      {change !== undefined && (
        <div className="mt-3 flex items-center gap-1.5">
          {isPositive ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {change}%
          </span>
          <span className="text-sm text-gray-500">vs last week</span>
        </div>
      )}
    </div>
  );
}

function ScheduledPost({ post }) {
  return (
    <div className="flex gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
      {post.media?.[0] ? (
        <img 
          src={post.media[0].url} 
          alt="" 
          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
          <Calendar className="w-6 h-6 text-gray-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
          {post.content}
        </p>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex items-center gap-1">
            {post.platforms.map(p => {
              const Icon = platformIcons[p] || Calendar;
              return (
                <Icon key={p} className={`w-4 h-4 ${platformColors[p]}`} />
              );
            })}
          </div>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {format(new Date(post.scheduledAt), 'MMM d, h:mm a')}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const { posts, getScheduledPosts } = usePostsStore();
  const { overview, platformStats } = useAnalyticsStore();
  const { getConnectedPlatforms } = usePlatformsStore();
  
  const scheduledPosts = getScheduledPosts();
  const connectedPlatforms = getConnectedPlatforms();
  
  const stats = [
    { title: 'Total Impressions', value: overview.totalImpressions, change: 12.5, changeType: 'positive', icon: Eye },
    { title: 'Engagements', value: overview.totalEngagements, change: 8.2, changeType: 'positive', icon: Heart },
    { title: 'Link Clicks', value: overview.totalClicks, change: -2.4, changeType: 'negative', icon: MousePointerClick },
    { title: 'Follower Growth', value: `+${overview.followerGrowth}`, change: 15.3, changeType: 'positive', icon: Users },
  ];
  
  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome back, {user?.name?.split(' ')[0] || 'there'}!
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Here's what's happening with your social presence.
        </p>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <StatCard key={i} {...stat} />
        ))}
      </div>
      
      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scheduled Posts */}
        <div className="lg:col-span-2 card">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">Upcoming Posts</h2>
              <p className="text-sm text-gray-500 mt-0.5">{scheduledPosts.length} posts scheduled</p>
            </div>
            <Link to="/create" className="btn-primary text-sm py-1.5 px-3">
              <Plus className="w-4 h-4 mr-1.5" />
              New Post
            </Link>
          </div>
          <div className="p-4 space-y-3">
            {scheduledPosts.length > 0 ? (
              <>
                {scheduledPosts.slice(0, 4).map(post => (
                  <ScheduledPost key={post.id} post={post} />
                ))}
                {scheduledPosts.length > 4 && (
                  <Link 
                    to="/calendar" 
                    className="flex items-center justify-center gap-2 py-3 text-sm text-brand-500 hover:text-brand-600 font-medium"
                  >
                    View all scheduled posts
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No posts scheduled</p>
                <Link to="/create" className="text-brand-500 hover:text-brand-600 text-sm font-medium mt-1 inline-block">
                  Create your first post
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {/* Platform Performance */}
        <div className="card">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <h2 className="font-semibold text-gray-900 dark:text-white">Platform Performance</h2>
            <p className="text-sm text-gray-500 mt-0.5">Last 30 days</p>
          </div>
          <div className="p-4 space-y-4">
            {platformStats.map((stat, i) => {
              const Icon = platformIcons[stat.platform] || Calendar;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 ${platformColors[stat.platform]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {stat.platform}
                    </p>
                    <p className="text-xs text-gray-500">
                      {stat.followers.toLocaleString()} followers
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {stat.impressions.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">impressions</p>
                  </div>
                </div>
              );
            })}
            
            {connectedPlatforms.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  No platforms connected yet
                </p>
                <Link 
                  to="/integrations" 
                  className="text-brand-500 hover:text-brand-600 text-sm font-medium"
                >
                  Connect accounts
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link 
          to="/create"
          className="card p-5 hover:border-brand-300 dark:hover:border-brand-700 transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 group-hover:scale-110 transition-transform">
              <Plus className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Create Post</p>
              <p className="text-sm text-gray-500">Schedule new content</p>
            </div>
          </div>
        </Link>
        
        <Link 
          to="/analytics"
          className="card p-5 hover:border-brand-300 dark:hover:border-brand-700 transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">View Analytics</p>
              <p className="text-sm text-gray-500">Track your performance</p>
            </div>
          </div>
        </Link>
        
        <Link 
          to="/integrations"
          className="card p-5 hover:border-brand-300 dark:hover:border-brand-700 transition-colors group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Connect Accounts</p>
              <p className="text-sm text-gray-500">{connectedPlatforms.length} of 8 connected</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
